import React, { createContext, useContext, useEffect, useState } from 'react';
import { getModules } from '@go-client/lib/api/modules';
import {
  ContextExtensionModule,
  ContextExtensions,
  GoModule,
  GoModuleSpec,
} from '@go-shared/types/go-modules/goModules';
import { ExtensionPointNamespace } from '@go-shared/types/go-modules/extensionPoints';

let ctxExtensionPoints: ContextExtensions = {
  [ExtensionPointNamespace.UI_MENU]: [],
};

interface ModuleContextType {
  modules: GoModuleSpec[];
  getExtensions: () => ContextExtensions;
  getExtensionsByNamespace: (namespace: ExtensionPointNamespace) => ContextExtensionModule[];
}

const ModuleContext = createContext<ModuleContextType | undefined>(undefined);

export const useModules = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModules should be used with ModuleProvider');
  }
  return context;
};

interface ModuleProviderProps {
  children?: React.ReactNode;
}

export default function ModuleProvider(props: ModuleProviderProps) {
  const { children } = props;
  const [modules, setModules] = useState<GoModuleSpec[]>([]);
  const [extensionPoints, setExtensionPoints] = useState<ContextExtensions>(ctxExtensionPoints);

  /*
    Get "extensions" from the Module Spec and store in the context
   */
  const registerExtensions = (modules: GoModuleSpec[]) => {
    for (let module of modules) {
      if (module.extensions) {
        for (const extensions of module.extensions) {
          const { namespace, contribution } = extensions;
          if (contribution) {
            ctxExtensionPoints[namespace].push({
              name: module.name,
              component: module.component,
              instance: module.instance,
              contribution,
            });
          }
        }
      }
    }

    setExtensionPoints(ctxExtensionPoints);
  };

  /*
    Return all the Extension Points registered
   */
  const getExtensions = (): ContextExtensions => extensionPoints;

  const getExtensionsByNamespace = (
    namespace: ExtensionPointNamespace
  ): ContextExtensionModule[] => {
    const extensions = getExtensions();

    return extensions[namespace] || [];
  };

  const initModules = async () => {
    try {
      const loadedModules: GoModuleSpec[] = [];
      const moduleList: GoModule[] = await getModules();

      console.log(`[Module Context] Modules: `, moduleList);

      for (let moduleData of moduleList) {
        const { name, enabled, component, permissions, extensions } = moduleData;

        // skip disabled modules
        if (!enabled) {
          console.log(`[Module Context] Skipping disabled module ${name}`);
          continue;
        }

        try {
          const module = await import(`../modules/${component.path}`);
          // const module = await import(
          //   /* webpackIgnore: true */ `../${moduleData.componentPath}.js`
          // );

          const ModuleComponent = module.default;
          const instance = new ModuleComponent();

          loadedModules.push({
            instance,
            name,
            enabled,
            component,
            permissions,
            extensions,
          });
        } catch (error) {
          console.error(`[Module Context] Error al cargar el plugin ${moduleData.name}:`, error);
        }
      }

      setModules(loadedModules);
      console.log('[Module Context] Loaded modules', loadedModules);

      // Register modules on each Extension Point
      registerExtensions(loadedModules);
    } catch (error) {
      console.error('[Module Context] Error getting modules from API:', error);
    }
  };

  // Load modules from API
  useEffect(() => {
    initModules().catch(error => console.error(`Error initializing modules: `, error));
  }, []);

  const contextValue: ModuleContextType = {
    modules,
    getExtensions,
    getExtensionsByNamespace,
  };

  return <ModuleContext.Provider value={contextValue}>{children}</ModuleContext.Provider>;
}
